import React from "react";

/**
 * BondMark — the BetBond identity glyph.
 *
 * Two peer dots bridged by an arc = a Bond. A second, fainter arc below gives
 * it dimension and lightly echoes an escrow ring. Built as a single SVG so it
 * scales cleanly from favicon size up to hero size.
 *
 * Props:
 *   size       — pixel height of the mark (width auto-scales).
 *   animated   — if true, the bond arc draws on mount & the dots pulse softly.
 *   accent     — bond / arc / accent colour (defaults to BetBond blue).
 *   peerColor  — colour of the two peer dots (defaults to white).
 *   className  — pass-through.
 *   title      — a11y title.
 */
const BondMark = ({
  size = 28,
  animated = false,
  accent = "#1E88E5",
  peerColor = "#ffffff",
  className = "",
  title = "BetBond",
}) => {
  // Intrinsic aspect ratio of the mark
  const VB_W = 64;
  const VB_H = 28;
  const width = (size * VB_W) / VB_H;

  return (
    <svg
      role="img"
      aria-label={title}
      width={width}
      height={size}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className={className}
      style={{ display: "block", overflow: "visible" }}
    >
      <defs>
        <linearGradient id="bm-arc" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={accent} stopOpacity="0.25" />
          <stop offset="50%" stopColor={accent} stopOpacity="1" />
          <stop offset="100%" stopColor={accent} stopOpacity="0.25" />
        </linearGradient>
        <radialGradient id="bm-dot" cx="0.35" cy="0.35" r="0.7">
          <stop offset="0%" stopColor={peerColor} stopOpacity="1" />
          <stop offset="100%" stopColor={peerColor} stopOpacity="0.78" />
        </radialGradient>
        <filter id="bm-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Counter-arc (escrow whisper) */}
      <path
        d="M 10 16 Q 32 26 54 16"
        fill="none"
        stroke={accent}
        strokeOpacity="0.22"
        strokeWidth="1.1"
        strokeLinecap="round"
      />

      {/* Main bond arc */}
      <path
        d="M 10 14 Q 32 -2 54 14"
        fill="none"
        stroke="url(#bm-arc)"
        strokeWidth="2"
        strokeLinecap="round"
        filter="url(#bm-glow)"
        style={
          animated
            ? {
                strokeDasharray: 80,
                strokeDashoffset: 80,
                animation: "bm-draw 1.1s ease-out 0.05s forwards",
              }
            : undefined
        }
      />

      {/* Peer dots */}
      <circle
        cx="10"
        cy="14"
        r="4.2"
        fill="url(#bm-dot)"
        className={animated ? "bm-dot bm-dot-l" : undefined}
      />
      <circle
        cx="54"
        cy="14"
        r="4.2"
        fill="url(#bm-dot)"
        className={animated ? "bm-dot bm-dot-r" : undefined}
      />

      <style>{`
        @keyframes bm-draw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes bm-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.08); }
        }
        .bm-dot { transform-box: fill-box; transform-origin: center; }
        .bm-dot-l { animation: bm-pulse 3.2s ease-in-out infinite; }
        .bm-dot-r { animation: bm-pulse 3.2s ease-in-out 1.6s infinite; }
      `}</style>
    </svg>
  );
};

/**
 * Full logo lockup: BondMark + wordmark, in a single inline-flex block.
 * Use this anywhere you'd previously have shown the "BetBond" wordmark.
 */
export const BondLockup = ({
  size = 28,
  className = "",
  textClassName = "",
  animated = false,
}) => (
  <span className={`inline-flex items-center gap-2.5 ${className}`}>
    <BondMark size={size} animated={animated} />
    <span
      className={`font-extrabold tracking-tight leading-none select-none ${textClassName}`}
    >
      <span className="text-white">Bet</span>
      <span className="text-[#1E88E5]">Bond</span>
    </span>
  </span>
);

export default BondMark;
